import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	DatePicker,
	Empty,
	Flex,
	Input,
	Modal,
	Space,
	Spin,
	Switch,
	Table,
	Tag,
	Typography,
	message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type {
	ManualEditBalloon,
	ManualEditForm,
	ManualEditInspector,
	ManualEditIssue,
	ManualEditStep,
} from '../../redux/api/traveler-audit';
import {
	useGetManualEditFormQuery,
	useManualEditDocumentMutation,
} from '../../redux/api/traveler-audit';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import {
	API_DATE,
	DISPLAY_DATE,
	MAX_INITIALS,
	type ManualDraft,
	type StampDraft,
	buildEdits,
	dayToDisplay,
	emptyDraft,
	faiDateToDay,
	faiKey,
	flaggedFirst,
	hasIssues,
	isMissing,
	normalizeInitials,
	visibleBalloons,
	worstSeverity,
} from './manualRecovery';

const { Text, Title } = Typography;

const ROW_BG = { fail: '#fff1f0', review: '#fffbe6' } as const;

const statusColor = (status?: string | null) => {
	if (status === 'pass') return 'green';
	if (status === 'review') return 'gold';
	if (status === 'fail') return 'red';
	if (status === 'complete') return 'blue';
	if (status === 'printed') return 'purple';
	return 'default';
};

const rowStyle = (issues: ManualEditIssue[]): CSSProperties | undefined => {
	const worst = worstSeverity(issues);
	return worst ? { background: ROW_BG[worst] } : undefined;
};

const labelStyle = (issues: ManualEditIssue[]): CSSProperties => ({
	fontWeight: issues.length ? 600 : 400,
});

const Current = ({ value }: { value?: string | null }) =>
	isMissing(value) ? <Text type="danger">N/A</Text> : <Text>{value}</Text>;

const IssueList = ({ issues }: { issues: ManualEditIssue[] }) =>
	issues.length ? (
		<Space direction="vertical" size={0}>
			{issues.map((issue, i) => (
				<Text
					key={`${i}-${issue.text}`}
					type={issue.severity === 'fail' ? 'danger' : 'warning'}
					style={{ fontSize: 12 }}
				>
					{issue.text}
				</Text>
			))}
		</Space>
	) : null;

function StampInputs({
	draft,
	disabled,
	onChange,
}: {
	draft: StampDraft | undefined;
	disabled: boolean;
	onChange: (next: StampDraft) => void;
}) {
	const value = draft ?? { initials: '', date: '' };
	return (
		<Space size={4}>
			<Input
				aria-label="Recovered initials"
				placeholder="Initials"
				maxLength={MAX_INITIALS}
				style={{ width: 90 }}
				value={value.initials}
				disabled={disabled}
				onChange={(e) =>
					onChange({ ...value, initials: normalizeInitials(e.target.value) })
				}
			/>
			<DatePicker
				aria-label="Recovered date"
				format={DISPLAY_DATE}
				placeholder={DISPLAY_DATE}
				style={{ width: 140 }}
				value={value.date ? dayjs(value.date) : null}
				disabled={disabled}
				onChange={(d) =>
					onChange({ ...value, date: d ? d.format(API_DATE) : '' })
				}
			/>
		</Space>
	);
}

type FaiRow = {
	/** Table row key; unique even when a balloon id repeats. */
	key: string;
	/** ``faiKey`` of the sign-off, the key into ``draft.fai``. */
	draftKey: string;
	balloon: ManualEditBalloon;
	inspector: ManualEditInspector | null;
	/** Rows the balloon cell spans (0 hides the cell on continuation rows). */
	span: number;
};

const faiRows = (balloons: ManualEditBalloon[]): FaiRow[] =>
	balloons.flatMap((balloon, position): FaiRow[] => {
		if (!balloon.inspectors.length) {
			return [
				{
					key: `${position}:none`,
					draftKey: '',
					balloon,
					inspector: null,
					span: 1,
				},
			];
		}
		return balloon.inspectors.map((inspector, i) => ({
			key: `${position}:${inspector.index}`,
			draftKey: faiKey(balloon.balloon, inspector.index),
			balloon,
			inspector,
			span: i === 0 ? balloon.inspectors.length : 0,
		}));
	});

function Section({
	title,
	extra,
	children,
}: {
	title: string;
	extra?: ReactNode;
	children: ReactNode;
}) {
	return (
		<div style={{ marginTop: 16 }}>
			<Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
				<Title level={5} style={{ margin: 0 }}>
					{title}
				</Title>
				{extra}
			</Flex>
			{children}
		</div>
	);
}

type Props = {
	runId: string;
	documentId: string | null;
	open: boolean;
	/** ``saved`` is true when at least one save went through while open. */
	onClose: (saved: boolean) => void;
};

export function ManualRecoveryModal({
	runId,
	documentId,
	open,
	onClose,
}: Props) {
	const { data, isFetching, error, refetch } = useGetManualEditFormQuery(
		{ runId, documentId: documentId ?? '' },
		{ skip: !open || !documentId, refetchOnMountOrArgChange: true }
	);
	const [save, { isLoading: isSaving }] = useManualEditDocumentMutation();
	const [draft, setDraft] = useState<ManualDraft>(emptyDraft);
	const [showAllBalloons, setShowAllBalloons] = useState(false);
	/** The POST result; shown until the next load so the re-check is visible. */
	const [saved, setSaved] = useState<ManualEditForm | null>(null);
	/** True once any save wrote a change while open (the table must refresh). */
	const [didSave, setDidSave] = useState(false);

	useEffect(() => {
		if (!open) {
			setDraft(emptyDraft());
			setShowAllBalloons(false);
			setSaved(null);
			setDidSave(false);
		}
	}, [open, documentId]);

	const form = saved ?? data;
	const edits = useMemo(
		() => (form ? buildEdits(form, draft) : []),
		[form, draft]
	);
	const disabled = !form?.editable;

	const setStep = (key: string, next: StampDraft) =>
		setDraft((d) => ({ ...d, steps: { ...d.steps, [key]: next } }));
	const setFai = (key: string, next: StampDraft) =>
		setDraft((d) => ({ ...d, fai: { ...d.fai, [key]: next } }));

	const handleSave = async () => {
		if (!form || !documentId || !edits.length) return;
		try {
			const result = await save({ runId, documentId, edits }).unwrap();
			setDraft(emptyDraft());
			if (!result.changed) {
				message.info('Nothing changed.');
				return;
			}
			setSaved(result);
			setDidSave(true);
			const text = `Saved ${result.changed} change${
				result.changed === 1 ? '' : 's'
			}. Packet is now ${result.status.toUpperCase()}.`;
			// Match the Alert below: a save that leaves the packet failing is a warning.
			if (result.status === 'fail') message.warning(text);
			else message.success(text);
		} catch (err) {
			if ((err as { status?: unknown })?.status === 409) {
				// Keep the draft; show the packet as it is now.
				message.warning(getApiErrorMessage(err, 'The packet changed.'));
				setSaved(null);
				void refetch();
				return;
			}
			message.error(getApiErrorMessage(err, 'Manual recovery failed.'));
		}
	};

	const requestClose = () => {
		if (isSaving) return;
		if (!edits.length) {
			onClose(didSave);
			return;
		}
		Modal.confirm({
			title: `Discard ${edits.length} unsaved change${
				edits.length === 1 ? '' : 's'
			}?`,
			okText: 'Discard',
			okButtonProps: { danger: true },
			cancelText: 'Keep editing',
			onOk: () => onClose(didSave),
		});
	};

	const stepColumns: ColumnsType<ManualEditStep> = [
		{
			title: 'Step',
			key: 'label',
			render: (_, step) => (
				<Space direction="vertical" size={0}>
					<Text style={labelStyle(step.issues)}>{step.label}</Text>
					<IssueList issues={step.issues} />
				</Space>
			),
		},
		{
			title: 'Current initials',
			key: 'user',
			width: 120,
			render: (_, step) => <Current value={step.user} />,
		},
		{
			title: 'Current date',
			key: 'at',
			width: 120,
			render: (_, step) =>
				step.raw_at ? (
					<Text type="secondary" title="Stored value is not a readable date">
						{step.raw_at}
					</Text>
				) : (
					<Current value={dayToDisplay(step.day)} />
				),
		},
		{
			title: 'Recovered initials / date',
			key: 'edit',
			width: 260,
			render: (_, step) => (
				<StampInputs
					draft={draft.steps[step.key]}
					disabled={disabled}
					onChange={(next) => setStep(step.key, next)}
				/>
			),
		},
		{
			title: '',
			key: 'use',
			width: 100,
			render: (_, step) => (
				<Button
					type="link"
					size="small"
					disabled={disabled || (isMissing(step.user) && !step.day)}
					onClick={() =>
						setStep(step.key, {
							initials: isMissing(step.user) ? '' : (step.user as string),
							date: step.day ?? '',
						})
					}
				>
					Use current
				</Button>
			),
		},
	];

	const faiColumns: ColumnsType<FaiRow> = [
		{
			title: 'Balloon',
			key: 'balloon',
			width: 220,
			onCell: (row) => ({ rowSpan: row.span }),
			render: (_, row) => (
				<Space direction="vertical" size={0}>
					<Text style={labelStyle(row.balloon.issues)}>
						{row.balloon.balloon}
						{row.balloon.visual ? ' (visual)' : ''}
					</Text>
					<IssueList issues={row.balloon.issues} />
				</Space>
			),
		},
		{
			title: 'Sample',
			key: 'sample',
			width: 140,
			render: (_, row) =>
				row.inspector ? (
					<Text>
						#{row.inspector.sample_number ?? row.inspector.index + 1}
						{row.inspector.value ? ` · ${row.inspector.value}` : ''}
					</Text>
				) : (
					<Text type="secondary">No inspector sign-offs recorded</Text>
				),
		},
		{
			title: 'Current initials',
			key: 'initials',
			width: 120,
			render: (_, row) =>
				row.inspector ? <Current value={row.inspector.initials} /> : null,
		},
		{
			title: 'Current date',
			key: 'date',
			width: 120,
			render: (_, row) =>
				row.inspector ? (
					<Current value={dayToDisplay(faiDateToDay(row.inspector.date))} />
				) : null,
		},
		{
			title: 'Recovered initials / date',
			key: 'edit',
			width: 260,
			render: (_, row) =>
				row.inspector ? (
					<StampInputs
						draft={draft.fai[row.draftKey]}
						disabled={disabled || !row.balloon.editable}
						onChange={(next) => setFai(row.draftKey, next)}
					/>
				) : null,
		},
	];

	const balloons = form ? visibleBalloons(form, showAllBalloons) : [];
	const flaggedBalloonCount = form ? form.fai.filter(hasIssues).length : 0;

	return (
		<Modal
			title="Manual recovery"
			open={open}
			width={1100}
			destroyOnClose
			maskClosable={false}
			closable={!isSaving}
			keyboard={!isSaving}
			onCancel={requestClose}
			footer={
				<Flex justify="space-between" align="center">
					<Text type={edits.length ? undefined : 'secondary'}>
						{edits.length} change{edits.length === 1 ? '' : 's'}
					</Text>
					<Space>
						<Button disabled={isSaving} onClick={requestClose}>
							Close
						</Button>
						<Button
							type="primary"
							disabled={disabled || !edits.length}
							loading={isSaving}
							onClick={() => void handleSave()}
						>
							Save
						</Button>
					</Space>
				</Flex>
			}
		>
			{error && !form ? (
				<Alert
					type="error"
					showIcon
					message={getApiErrorMessage(error, 'Could not load the packet.')}
				/>
			) : !form ? (
				<Flex justify="center" style={{ padding: 48 }}>
					<Spin />
				</Flex>
			) : (
				<Spin spinning={isFetching && !saved}>
					<Flex gap="small" align="center" wrap="wrap">
						<Text strong>{form.filename}</Text>
						<Tag color={statusColor(form.status)}>
							{form.status.toUpperCase()}
						</Tag>
						<Text type="secondary">
							{form.failures.length} failure
							{form.failures.length === 1 ? '' : 's'} · {form.lookouts.length}{' '}
							lookout{form.lookouts.length === 1 ? '' : 's'}
						</Text>
					</Flex>
					{saved && (
						<Alert
							style={{ marginTop: 8 }}
							type={form.status === 'fail' ? 'warning' : 'success'}
							showIcon
							message={`Saved. Re-checked packet is ${form.status.toUpperCase()}.`}
						/>
					)}
					{!form.editable && form.locked_reason && (
						<Alert
							style={{ marginTop: 8 }}
							type="info"
							showIcon
							message={form.locked_reason}
						/>
					)}
					{form.editable && !form.has_layer && (
						<Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
							Showing live ERP values. Saving freezes them into a packet layer
							first.
						</Text>
					)}

					<Section title="Traveler steps">
						<Table
							size="small"
							rowKey="key"
							pagination={false}
							columns={stepColumns}
							dataSource={flaggedFirst(form.steps)}
							onRow={(step) => ({ style: rowStyle(step.issues) })}
						/>
					</Section>

					<Section title="Revision and material certificate">
						<Flex gap="large" wrap="wrap">
							<Space
								direction="vertical"
								size={4}
								style={{
									padding: 8,
									borderRadius: 6,
									...rowStyle(form.revision.issues),
								}}
							>
								<Text>
									Revision: <Current value={form.revision.value} />
									{form.revision.part_number_rev && (
										<Text type="secondary">
											{' '}
											(part number says {form.revision.part_number_rev})
										</Text>
									)}
								</Text>
								<Input
									aria-label="Recovered revision"
									placeholder="Recovered revision"
									style={{ width: 220 }}
									value={draft.revision}
									disabled={disabled}
									onChange={(e) =>
										setDraft((d) => ({ ...d, revision: e.target.value }))
									}
								/>
								<IssueList issues={form.revision.issues} />
							</Space>
							<Space
								direction="vertical"
								size={4}
								style={{
									padding: 8,
									borderRadius: 6,
									...rowStyle(form.material_cert.issues),
								}}
							>
								<Text>
									Material certificate:{' '}
									{form.material_cert.has_certificate === false ? (
										<Text type="danger">missing</Text>
									) : form.material_cert.has_certificate ? (
										<Text>on file</Text>
									) : (
										<Text type="secondary">unknown</Text>
									)}
								</Text>
								<Checkbox
									checked={draft.certNa ?? form.material_cert.not_applicable}
									disabled={disabled}
									onChange={(e) =>
										setDraft((d) => ({ ...d, certNa: e.target.checked }))
									}
								>
									Material cert N/A
								</Checkbox>
								<IssueList issues={form.material_cert.issues} />
							</Space>
						</Flex>
					</Section>

					<Section
						title={`FAI inspectors (${flaggedBalloonCount} flagged of ${form.fai.length})`}
						extra={
							form.fai.length > 0 && (
								<Space size={4}>
									<Switch
										size="small"
										checked={showAllBalloons}
										onChange={setShowAllBalloons}
									/>
									<Text>Show all balloons</Text>
								</Space>
							)
						}
					>
						{balloons.length ? (
							<Table
								size="small"
								rowKey="key"
								pagination={false}
								columns={faiColumns}
								dataSource={faiRows(balloons)}
								onRow={(row) => ({ style: rowStyle(row.balloon.issues) })}
								scroll={{ y: 360 }}
							/>
						) : (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								description={
									form.fai.length
										? 'No flagged balloons.'
										: 'No FAI data on this packet.'
								}
							/>
						)}
					</Section>

					{form.other_issues.length > 0 && (
						<Section title="Other issues">
							<Text
								type="secondary"
								style={{ display: 'block', marginBottom: 4 }}
							>
								These are not editable here. Use Run recovery or fix them in the
								ERP.
							</Text>
							<Space direction="vertical" size={2}>
								{form.other_issues.map((issue, i) => (
									<Space key={`${i}-${issue.text}`} size={4}>
										<Tag color={issue.severity === 'fail' ? 'red' : 'gold'}>
											{issue.severity === 'fail' ? 'Fail' : 'Review'}
										</Tag>
										<Text>{issue.text}</Text>
									</Space>
								))}
							</Space>
						</Section>
					)}
				</Spin>
			)}
		</Modal>
	);
}
